import { isValid } from "date-fns";
import { EMPTY } from "@/constants/index";
import type { IsoDate } from "@/ui-kit";
import { formatIsoDate, isoDateOf, useDateLocale } from "@/ui-kit";
import { DateTimeUtils } from "@/utils/DateTimeUtils";

type IProps = {
  value?: number | string | Date | null | undefined;
  time?: boolean;
  /** The string is a moment (created/updated on, meeting start), not a date-only value. */
  instant?: boolean;
};

/**
 * A string is the API's date-only value: its calendar day, never shifted by the zone.
 * An instant string is shown on the user's day.
 */
const dayOf = (
  value: number | string | Date,
  instant?: boolean,
): IsoDate | null => {
  if (typeof value === "string") {
    return instant
      ? DateTimeUtils.localDayOf(value)
      : DateTimeUtils.fromApiDate(value);
  }
  const date = value instanceof Date ? value : new Date(value);
  return isValid(date) ? isoDateOf(date) : null;
};

const timeFormats = new Map<string, Intl.DateTimeFormat>();

/** "10:00 PM" in the US: the locale picks the 12- or 24-hour clock. */
const timeFormatOf = (locale: string): Intl.DateTimeFormat => {
  let format = timeFormats.get(locale);
  if (!format) {
    format = new Intl.DateTimeFormat(locale, {
      hour: "numeric",
      minute: "2-digit",
    });
    timeFormats.set(locale, format);
  }
  return format;
};

export const FormattedDate = ({ value, time, instant }: IProps) => {
  const { formats, displayLocale } = useDateLocale();

  if (!value) {
    return EMPTY;
  }

  const day = dayOf(value, instant);

  if (!day) {
    return EMPTY;
  }

  // A moment, not a calendar day: the day and the time both come from the local clock,
  // or an evening in New York shows the next UTC day.
  if (time && typeof value === "string") {
    const moment = DateTimeUtils.parseApiInstant(value);

    if (moment) {
      const localDate = formatIsoDate(isoDateOf(moment), formats.long);
      return `${localDate}, ${timeFormatOf(displayLocale).format(moment)}`;
    }
  }

  // "October 7, 2026" in the US, "7 October 2026" in Europe: never an ambiguous 10/07.
  return formatIsoDate(day, formats.long);
};
